import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientTranslation } from '@/lib/core/i18n';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { ImagesToImageMap } from '@/lib/core/utils';
import { Heading } from '@/components/ui/heading';
import { Button } from '@/components/ui/button';
import { ScreenHead } from '@/components/shared/screen/head';
import { BackgroundVideo } from '@/components/shared/video/background';
import {
  LocationDicoveryElementModelContentType,
  type ExploreIntroContentResponseModel,
  type ExploreIntroPropertiesModel,
  type IApiContentResponseModelBase,
  type LocationDicoveryElementModel,
  type LocationDicoveryPropertiesModel,
} from '@/services/platform/api/model';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ExploreIntroScreenProps {
  content?: ExploreIntroContentResponseModel;
}

interface LocationBlock {
  key: string;
  locationName: string;
  locationNodeKey: string;
  discoverPath: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function extractLocationBlocks(props: ExploreIntroPropertiesModel | undefined): LocationBlock[] {
  return (props?.components?.items ?? [])
    .map(item => item.content as LocationDicoveryElementModel | undefined)
    .filter(
      (c): c is LocationDicoveryElementModel =>
        c?.contentType === LocationDicoveryElementModelContentType.locationDicovery
    )
    .flatMap(c => {
      const p = c.properties as LocationDicoveryPropertiesModel | undefined;
      const locationName = p?.locationName;
      const locationNodeKey = (p?.location?.[0] as IApiContentResponseModelBase | undefined)?.id;
      const link = p?.discoveryLink?.[0];
      const discoverPath = link?.route?.path ?? link?.url ?? undefined;
      if (!locationName || !locationNodeKey || !discoverPath) return [];
      return [{ key: c.id, locationName, locationNodeKey, discoverPath }];
    });
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ExploreIntroScreen({ content }: ExploreIntroScreenProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const setMainClassName = useUIStore(state => state.setMainClassName);
  const setNavStyleOverride = useUIStore(state => state.setNavStyleOverride);

  useEffect(() => {
    setMainClassName('pb-0');
    setNavStyleOverride('default');
    return () => {
      setMainClassName('');
      setNavStyleOverride(null);
    };
  }, [setMainClassName, setNavStyleOverride]);

  const props = content?.properties as ExploreIntroPropertiesModel | undefined;
  const bgImage = ImagesToImageMap(props?.pageHero);
  const isVideo = bgImage?.type === 'umbracoMediaVideo';
  const title = props?.title;
  const titleHeading = (props?.titleHeading?.toLowerCase() ?? 'h2') as React.ElementType;
  const subtitle = props?.subtitle;

  const locationBlocks = extractLocationBlocks(props);

  function navigateToDiscover(block: LocationBlock) {
    navigate(block.discoverPath, { state: { locationNodeKey: block.locationNodeKey } });
  }

  // Dynamic grid: odd count → last card spans full width
  const isOddCount = locationBlocks.length > 0 && locationBlocks.length % 2 !== 0;

  return (
    <div
      className="relative min-h-svh w-full overflow-hidden"
      style={
        bgImage?.src && !isVideo
          ? { backgroundImage: `url(${bgImage.src})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : { backgroundColor: 'var(--color-jncquoi-neutral-1000)' }
      }
    >
      {/* Full-bleed video background */}
      {isVideo && <BackgroundVideo src={bgImage?.src} />}

      {/* Fixed top header — gradient + title */}
      <div className="pt-safe-area to-jncquoi-neutral-1000 absolute top-0 right-0 left-0 z-10 bg-linear-to-t from-transparent from-0% to-[81.25%] pb-4 backdrop-blur-[2.5px]">
        <ScreenHead
          title={t('App.Explore.Title')}
          onBack={() => navigate(-1)}
          ariaLabel={t('App.Labels.GoBack')}
          variant="overlay"
          className="pt-0"
        />
      </div>

      {/* Gradient overlay for legibility of bottom content */}
      <div
        aria-hidden="true"
        className="to-jncquoi-neutral-1000/70 pointer-events-none absolute inset-0 bg-linear-to-b from-transparent from-40%"
      />

      {/* Title + subtitle — centered, anchored above the location grid */}
      {(title || subtitle) && (
        <div className="absolute right-4 bottom-[calc(228px+((env(safe-area-inset-bottom)+1.5rem)*2))] left-4 flex flex-col items-center text-center">
          {title && (
            <Heading
              as={titleHeading}
              className="font-pp-neue-montreal text-jncquoi-neutral-00 text-[24px] font-semibold tracking-[0.5px] uppercase"
            >
              {title}
            </Heading>
          )}
          {subtitle && (
            <p className="font-didot text-jncquoi-neutral-00 text-[36px] uppercase italic">{subtitle}</p>
          )}
        </div>
      )}

      {/* Location grid — pinned to the bottom */}
      {locationBlocks.length > 0 && (
        <div className="absolute right-0 bottom-0 left-0 px-4 pb-[calc(((env(safe-area-inset-bottom)+1.5rem)*2)+48px)]">
          <div className="grid grid-cols-2 gap-2">
            {locationBlocks.map((block, index) => {
              const isLastOdd = isOddCount && index === locationBlocks.length - 1;
              return (
                <Button
                  key={block.key}
                  variant="secondary-outline"
                  palette="white"
                  size="lg"
                  className={['w-full', isLastOdd ? 'col-span-2' : ''].filter(Boolean).join(' ')}
                  onClick={() => navigateToDiscover(block)}
                >
                  {block.locationName}
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
